import { DatastoreInterface } from '@freelancer/datastore/core';
import { generateNumbersInRangeWithDuplicates } from '@freelancer/datastore/testing';
import { RECOMMENDED_CUTOFF } from '../bids/bids.backend-model';
import { Bid } from '../bids/bids.model';
import { ProjectViewBid } from './project-view-bids.model';
import { ProjectViewBidsCollection } from './project-view-bids.types';

interface GenerateBidsOptions {
  readonly bids: ReadonlyArray<Bid>;
  readonly minScore?: number;
  readonly maxScore?: number;
}

export async function createProjectViewBids(
  datastore: DatastoreInterface,
  options: GenerateBidsOptions,
) {
  const objects = generateProjectViewBidObjects(options);
  await Promise.all(
    objects.map(object =>
      datastore.createRawDocument<ProjectViewBidsCollection>(
        'projectViewBids',
        object,
      ),
    ),
  );

  return objects;
}

function generateProjectViewBidObjects({
  bids,
  minScore = 0,
  maxScore = 1,
}: GenerateBidsOptions): ReadonlyArray<ProjectViewBid> {
  const scores = generateNumbersInRangeWithDuplicates(
    minScore,
    maxScore,
    bids.length,
    'bidScores',
  );

  const actualMaxScore = Math.max(...scores);

  /**
   * There could be more than one with the max score.
   * If so we arbitrarily pick the first.
   * The score must also be above RECOMMENDED_CUTOFF to be recommended.
   */
  const recommendedBidIndex = scores.findIndex(
    score => score === actualMaxScore && score > RECOMMENDED_CUTOFF,
  );

  return bids.map((bid, index) => ({
    ...bid,
    score: scores[index],
    recommended: index === recommendedBidIndex,
  }));
}
